import Vue from 'vue';
import Router from 'vue-router';

import store from '@/store';

import { deepCopy, buildLocalizedRoute } from '@/util';

let routeLanguages = store.state.bmr.availableLanguages;

let ActivityDetail = () => import('@/components/activities/ActivityDetail'),
    ActivityList = () => import('@/components/activities/ActivityList'),
    BookingDetail = () => import('@/components/bookings/BookingDetail'),
    BookingList = () => import('@/components/bookings/BookingList'),
    BusinessHours = () => import('@/components/store/BusinessHours'),
    DirectoryList = () => import('@/components/directory/DirectoryList'),
    EmployeeForm = () => import('@/components/employee/EmployeeForm'),
    EmployeeList = () => import('@/components/employee/EmployeeList'),
    Home = () => import('@/components/home/Home'),
    LearningDetail = () => import('@/components/learnings/LearningDetail'),
    LearningList = () => import('@/components/learnings/LearningList'),
    Login = () => import('@/components/login/Login'),
    MarketConditionDetail = () => import('@/components/market-conditions/MarketConditionDetail'),
    NotificationList = () => import('@/components/notifications/NotificationList'),
    NewsList = () => import('@/components/news/NewsList'),
    NewsDetail = () => import('@/components/news/NewsDetail'),
    NoticeDetail = () => import('@/components/notices/NoticeDetail'),
    NoticeList = () => import('@/components/notices/NoticeList'),
    PriceChangeList = () => import('@/components/price-change/PriceChangeList'),
    ProductPlacementList = () => import('@/components/bookings/ProductPlacementList'),
    PublicHoliday = () => import('@/components/store/PublicHoliday'),
    StaticDetail = () => import('@/components/static/StaticDetail'),
    StaticList = () => import('@/components/static/StaticList'),
    StoreProfile = () => import('@/components/store/Profile'),
    UserProfile = () => import('@/components/user/Profile');

let i18nRoutes = [
    {
        name: 'Home',
        component: Home,
        path: {
            en: 'home',
            fr: 'accueil'
        }
    },
    {
        name: 'Login',
        component: Login,
        meta: {
            'showHeader': false,
            'showFooter': false,
            'loginRequired': false
        },
        path: {
            en: 'login',
            fr: 'connexion'
        }
    },
    {
        name: 'NewsList',
        meta: {
            viewConditions: [
                'newsarticle'
            ]
        },
        component: NewsList,
        path: {
            en: 'news',
            fr: 'nouvelles'
        }
    },
    {
        name: 'NewsDetail',
        meta: {
            viewConditions: [
                'newsarticle'
            ],
            skipLanguageUpdate: true
        },
        component: NewsDetail,
        path: {
            en: 'news/:slug',
            fr: 'nouvelles/:slug'
        }
    },
    {
        name: 'DirectoryList',
        component: DirectoryList,
        path: {
            en: 'directory',
            fr: 'bottin'
        }
    },
    {
        name: 'ActivityList',
        meta: {
            viewConditions: [
                'activity'
            ]
        },
        component: ActivityList,
        path: {
            en: 'activities',
            fr: 'activites'
        }
    },
    {
        name: 'ActivityDetail',
        meta: {
            viewConditions: [
                'activity'
            ],
            skipLanguageUpdate: true
        },
        component: ActivityDetail,
        path: {
            en: 'activities/:slug',
            fr: 'activites/:slug'
        }
    },
    {
        name: 'ProductPlacementList',
        component: ProductPlacementList,
        meta: {
            viewConditions: [
                'booking'
            ]
        },
        path: {
            en: 'product-placements',
            fr: 'placements-de-produits'
        }
    },
    {
        name: 'Profile',
        component: UserProfile,
        path: {
            en: 'profile',
            fr: 'profil'
        }
    },
    {
        name: 'EmployeeList',
        component: EmployeeList,
        meta: {
            viewConditions: [
                'store'
            ]
        },
        path: {
            en: 'employees',
            fr: 'employes'
        }
    },
    {
        name: 'CreateEmployee',
        component: EmployeeForm,
        meta: {
            viewConditions: [
                'store'
            ]
        },
        path: {
            en: 'employees/create',
            fr: 'employes/creer'
        }
    },
    {
        name: 'EditEmployee',
        component: EmployeeForm,
        meta: {
            viewConditions: [
                'store'
            ]
        },
        path: {
            en: 'employees/edit/:employeeId',
            fr: 'employes/modifier/:employeeId'
        }
    },
    {
        component: StoreProfile,
        meta: {
            viewConditions: [
                'store'
            ]
        },
        path: {
            en: 'store',
            fr: 'magasin'
        },
        children: [
            {
                name: 'StoreProfile',
                component: BusinessHours,
                'redirect': {
                    name: 'BusinessHours'
                }
            },
            {
                name: 'BusinessHours',
                component: BusinessHours,
                meta: {
                    key: 'store-profile'
                },
                path: {
                    en: 'store/business-hours',
                    fr: 'magasin/heures-ouvertures'
                }
            },
            {
                name: 'PublicHoliday',
                component: PublicHoliday,
                meta: {
                    key: 'store-profile'
                },
                path: {
                    en: 'store/public-holidays',
                    fr: 'magasin/conges-feries'
                }
            }
        ]
    },
    {
        name: 'BookingList',
        component: BookingList,
        meta: {
            viewConditions: ['booking']
        },
        path: {
            en: 'bookings',
            fr: 'placements'
        }
    },
    {
        name: 'BookingDetail',
        component: BookingDetail,
        meta: {
            skipLanguageUpdate: true,
            viewConditions: ['booking']
        },
        path: {
            en: 'bookings/:slug',
            fr: 'placements/:slug'
        }
    },
    {
        name: 'NotificationList',
        component: NotificationList,
        path: 'notifications'
    },
    {
        name: 'NotificationListDate',
        component: NotificationList,
        path: 'notifications/:start'
    },
    {
        name: 'SingleStaticDetail',
        component: StaticDetail,
        meta: {
            skipLanguageUpdate: true,
            single: true
        },
        path: 'page/:type'
    },
    {
        name: 'SingleStaticList',
        component: StaticDetail,
        meta: {
            skipLanguageUpdate: true,
            single: true,
            hasHomePage: true
        },
        path: 'pages/:type'
    },
    {
        name: 'StaticList',
        component: StaticList,
        meta: {
            skipLanguageUpdate: true,
            viewConditions: [
                'page'
            ]
        },
        path: 'pages/:type'
    },
    {
        name: 'StaticDetail',
        component: StaticDetail,
        meta: {
            skipLanguageUpdate: true,
            viewConditions: [
                'page'
            ]
        },
        path: 'pages/:type/:slug'
    },
    {
        name: 'PriceChangeList',
        component: PriceChangeList,
        meta: {
            viewConditions: [
                'notice'
            ]
        },
        path: {
            en: 'price-change',
            fr: 'changement-de-prix'
        }
    },
    {
        name: 'NoticeList',
        component: NoticeList,
        meta: {
            viewConditions: [
                'notice'
            ],
            skipLanguageUpdate: true
        },
        path: {
            en: 'notices/:type',
            fr: 'avis/:type'
        }
    },
    {
        name: 'NoticeDetail',
        component: NoticeDetail,
        meta: {
            viewConditions: [
                'notice'
            ],
            skipLanguageUpdate: true
        },
        path: {
            en: 'notices/:type/:slug',
            fr: 'avis/:type/:slug'
        }
    },
    {
        name: 'MarketConditionDetail',
        component: MarketConditionDetail,
        meta: {
            viewConditions: ['marketcondition']
        },
        path: {
            en: 'market-conditions',
            fr: 'tendances-du-marche'
        }
    },
    {
        name: 'LearningList',
        meta: {
            viewConditions: [
                'learning'
            ]
        },
        component: LearningList,
        path: {
            en: 'learnings',
            fr: 'formations'
        }
    },
    {
        name: 'LearningDetail',
        meta: {
            viewConditions: [
                'learning'
            ],
            skipLanguageUpdate: true
        },
        component: LearningDetail,
        path: {
            en: 'learnings/:slug',
            fr: 'formations/:slug'
        }
    },
];

let storePlanningPages = [
    {
        name: 'PlanningGuide',
        path: {
            en: 'planning-guides',
            fr: 'guides-amenagement'
        }
    },
    {
        name: 'Merchandising',
        path: {
            en: 'merchandising',
            fr: 'mise-en-marche'
        }
    },
    {
        name: 'EndcapsAndPallets',
        path: {
            en: 'endcaps-and-pallets',
            fr: 'bouts-d-ilots-et-palettes'
        }
    },
    {
        name: 'Planograms',
        path: {
            en: 'planograms',
            fr: 'planographie'
        }
    },
    {
        name: 'ImagineProgram',
        path: {
            en: 'imagine-program',
            fr: 'programme-imagine'
        }
    },
];

let tacticalPages = [
    {
        name: 'Tactical',
        path: {
            en: 'tactical',
            fr: 'tactical'
        }
    },
];

Vue.use(Router);

let allRoutes = [];

for (let language of routeLanguages) {
    for (let route of i18nRoutes) {
        let languageRoute = deepCopy(route);
        allRoutes.push(buildLocalizedRoute(languageRoute, language));
    }
}

allRoutes.push({ path: '*' });

const router = new Router({
    mode: 'history',
    hash: false,
    scrollBehavior: (to, from, savedPosition) => {
        if (savedPosition) {
            return savedPosition;
        } else if (to.hash) {
            return { selector: to.hash };
        } else {
            // Scroll top only if parent route change
            if (from.matched && from.matched.length && from.matched[0].path != to.matched[0].path) {
                return { x: 0, y: 0 };
            }
        }
    },
    routes: allRoutes
});

router.beforeEach((to, from, next) => {
    if (to.matched.some(route => route.meta.loginRequired !== false)) {
        if (!window.localStorage.getItem('token')) {
            store.commit('bmr/SET_LAST_ROUTE', to);
            return next({ name: `Login-${store.state.bmr.language}` });
        }
        if (!to.matched.every(route => {
            let authorizedPage = false;
            let imagineAccess = store.state.bmr.user.access_right_group !== 'undefined' && store.state.bmr.user.access_right_group === 'Imagine';
            let tacticalAccess = store.state.bmr.user.access_right_group !== 'undefined' && store.state.bmr.user.access_right_group === 'Tactical';
            if (typeof to.params['type'] !== 'undefined') {
                if (tacticalAccess) {
                    for (let page of tacticalPages) {
                        if (page.path[`${store.state.bmr.language}`] == to.params['type']) {
                            authorizedPage = true;
                            break;
                        }
                    }
                } else if (imagineAccess) {
                    for (let page of storePlanningPages) {
                        if (page.path[`${store.state.bmr.language}`] == to.params['type']) {
                            authorizedPage = true;
                            break;
                        }
                    }
                }
            } else if (to.name !== `Home-${store.state.bmr.language}`) {
                if (to.name !== `BusinessHours-${store.state.bmr.language}`
                    && to.name !== `EmployeeList-${store.state.bmr.language}`
                    && to.name !== `Profile-${store.state.bmr.language}`
                    && to.name !== `CreateEmployee-${store.state.bmr.language}`
                    && to.name !== `EditEmployee-${store.state.bmr.language}`) {
                    if (tacticalAccess) {
                        for (let page of tacticalPages) {
                            if (page.path[`${store.state.bmr.language}`] == to.params['type']) {
                                authorizedPage = true;
                                break;
                            }
                        }
                    } else if (imagineAccess) {
                        for (let page of storePlanningPages) {
                            if (page.path[`${store.state.bmr.language}`] == to.params['type']) {
                                authorizedPage = true;
                                break;
                            }
                        }
                    }
                } else {
                    authorizedPage = true;
                }
            }
            if (authorizedPage) {
                if (typeof route.meta !== 'undefined' && typeof route.meta.viewConditions !== 'undefined') {
                    return route.meta.viewConditions.every(condition => store.state.bmr.user.permissions.includes(condition));
                }
                return true;
            } else {
                if (imagineAccess) {
                    return next({ name: 'StaticList-'+store.state.bmr.language, params: { type: store.state.bmr.language === 'fr' ? 'planographie' : 'planograms' }});
                } else if (tacticalAccess) {
                    return next({ name: 'SingleStaticDetail-'+store.state.bmr.language, params: { type: 'tactical' }});
                }
            }
            return true;
        })) {
            if (store.state.bmr.user.access_right_group !== 'undefined' && (store.state.bmr.user.access_right_group !== 'Imagine' && store.state.bmr.user.access_right_group !== 'Tactical')) {
                return next({ name: `Home-${store.state.bmr.language}` });
            }
            if (store.state.bmr.user.access_right_group !== 'undefined' && store.state.bmr.user.access_right_group === 'Imagine') {
                return next({ name: 'StaticList-'+store.state.bmr.language, params: { type: store.state.bmr.language === 'fr' ? 'planographie' : 'planograms' }});
            }
            if (store.state.bmr.user.access_right_group !== 'undefined' && store.state.bmr.user.access_right_group === 'Tactical') {
                return next({ name: 'SingleStaticDetail-'+store.state.bmr.language, params: { type: 'tactical' }});
            }
        }
    }

    if (to.matched.length === 1 && to.matched[0].path === '*') {
        return next({ name: `Home-${store.state.bmr.language}` });
    }

    if (to && to.meta.language && to.meta.language !== store.state.bmr.language) {
        store.dispatch('bmr/toggleLanguage', false);
    }

    return next();
});

router.afterEach((to, from) => {
    if (GA_TRACKING_ID && window.gtag) {
        if (store && store.state && store.state.bmr && store.state.bmr.user && store.state.bmr.user.id) {
            window.gtag('set', {
                dimension1: store.state.bmr.user.id
            });
        }
        window.gtag('config', GA_TRACKING_ID, {
            page_path: to.fullPath,
            app_name: 'L\'Atelier',
            send_page_view: true,
        });
    }

    if (from.fullPath !== '/') {
        store.commit('bmr/SET_LAST_ROUTE', from);
    }

    store.commit('bmr/SET_CURRENT_ROUTE', to);
});

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js